// Free calculators for futures and prop firm traders (no account needed): position size, risk
// plan, drawdown (max contracts) and the consistency rule. Each shows example values first, the
// answer in a card beside the inputs (a bar at the bottom on phones) and how the math works.
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { PRESETS } from './balance';
import { PublicShell, localPath, usePageLanguage, useTitle } from './PublicPage';
import { CONTRACTS, findContract, toTicks } from './tools/contracts';
import TOOLS from './tools/meta.json';
import { useI18n } from './i18n';
import { trackTool } from './analytics';
import './tools.css';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n) => usd.format(n);
const fmt = (n, digits = 2) => Number(n.toFixed(digits)).toLocaleString('en-US', { maximumFractionDigits: digits });

// Reads "7,795.25" or "7795.25"; empty or invalid gives null.
function num(value) {
  const n = parseFloat(String(value).replace(/[, $]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function toolPath(lang, slug = '') {
  return localPath(lang, `/tools${slug ? `/${slug}` : ''}`);
}

// Whole dollars without cents ("$200"), otherwise cents ("$40.50").
const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const cash = (n) => (Math.abs(n - Math.round(n)) < 0.005 ? usd0.format(Math.round(n)) : money(n));
const perPoint = (point) => `$${point.toLocaleString('en-US', { maximumFractionDigits: 2 })}/pt`;

// One state object per calculator, so Reset and "is this still the example?" stay simple.
function useValues(start) {
  const [v, setV] = useState(start);
  const set = (key) => (value) => setV((o) => ({ ...o, [key]: value }));
  return [v, set, setV];
}
const sameAs = (v, example) => Object.keys(example).every((k) => String(v[k]) === String(example[k]));

function Field({ label, hint, optional, children }) {
  const { t } = useI18n();
  return (
    <label className="tool-field">
      <span className="tool-label">{label}{optional && <span className="calc-optional">{t('optional')}</span>}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

function NumberInput({ value, onChange, placeholder, prefix, suffix, wide }) {
  return (
    <span className={`tool-input${prefix ? ' has-prefix' : ''}${suffix ? ' has-suffix' : ''}${wide ? ' wide-suffix' : ''}`}>
      {prefix && <span className="tool-prefix">{prefix}</span>}
      <input type="text" inputMode="decimal" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      {suffix && <span className="tool-suffix">{suffix}</span>}
    </span>
  );
}

// The four contracts most traders use as chips, the rest in "More…".
const QUICK = ['MNQ', 'MES', 'NQ', 'ES'];

function ContractChips({ value, onChange }) {
  const { t } = useI18n();
  const c = findContract(value);
  const quick = QUICK.includes(value);
  return (
    <div className="tool-field">
      <span className="calc-label-row">
        <span className="tool-label">{t('Contract')}</span>
        <span className="calc-label-note">{c.name} · {perPoint(c.point)}</span>
      </span>
      <div className="calc-chips" role="radiogroup" aria-label={t('Contract')}>
        {QUICK.map((sym) => (
          <button key={sym} type="button" role="radio" aria-checked={value === sym} className={`calc-chip${value === sym ? ' on' : ''}`} onClick={() => onChange(sym)}>
            <strong>{sym}</strong> <span>{perPoint(findContract(sym).point)}</span>
          </button>
        ))}
        <select className={`calc-chip calc-chip-more${quick ? '' : ' on'}`} value={quick ? '' : value} aria-label={t('More contracts')}
          onChange={(e) => e.target.value && onChange(e.target.value)}>
          <option value="">{t('More…')}</option>
          {CONTRACTS.filter((x) => !QUICK.includes(x.symbol)).map((x) => (
            <option key={x.symbol} value={x.symbol}>{x.symbol} · {x.name} ({perPoint(x.point)})</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// Options are [id, text, tone?]; a tone colors the chosen one (Long green, Short red).
function Toggle({ value, options, onChange, label, small }) {
  return (
    <div className={`calc-toggle${small ? ' small' : ''}`} role="radiogroup" aria-label={label}>
      {options.map(([id, text, tone]) => (
        <button key={id} type="button" role="radio" aria-checked={value === id}
          className={`${value === id ? `on${tone ? ` tone-${tone}` : ''}` : ''}`} onClick={() => onChange(id)}>
          {text}
        </button>
      ))}
    </div>
  );
}

function ExampleLine({ touched, onReset }) {
  const { t, lang } = useI18n();
  return (
    <p className="calc-example">
      <span className={`calc-dot${touched ? ' mine' : ''}`} aria-hidden="true" />
      {touched ? t('Your numbers') : t('Example values, change any number')}
      <button type="button" className="calc-reset" onClick={onReset}>{touched ? t('Reset to the example') : lang === 'es' ? 'Restablecer' : 'Reset'}</button>
    </p>
  );
}

function MoreOptions({ note, children }) {
  const { t } = useI18n();
  return (
    <details className="calc-more-options">
      <summary><span className="calc-plus" aria-hidden="true">+</span> {t('More options')} {note && <span className="calc-more-note">{note}</span>}</summary>
      <div className="calc-more-body">{children}</div>
    </details>
  );
}

function Tiles({ items }) {
  return (
    <div className="calc-tiles">
      {items.filter(Boolean).map(([value, label, tone]) => (
        <div key={label} className={`calc-tile${tone ? ` tone-${tone}` : ''}`}>
          <strong>{value}</strong>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

// Horizontal bars: each row's fill is a share (0 to 1) of the track; `marker` draws the gold line.
function Bars({ label, rows, marker, legend }) {
  return (
    <div className="calc-bars-wrap">
      {label && <span className="calc-section-label">{label}</span>}
      <ul className="calc-bars">
        {rows.map((r) => (
          <li key={r.key} className={`calc-bar-row tone-${r.tone}${r.best ? ' best' : ''}`}>
            <span className="calc-bar-name">{r.name}</span>
            <span className="calc-bar-track">
              <span className="calc-bar-fill" style={{ width: `${Math.max(2, Math.min(1, r.share) * 100)}%` }} />
              {marker !== undefined && <span className="calc-bar-marker" style={{ left: `${Math.min(1, marker) * 100}%` }} />}
            </span>
            <span className="calc-bar-value">{r.value}</span>
          </li>
        ))}
      </ul>
      {legend && <p className="calc-legend">{legend}</p>}
    </div>
  );
}

function SaveRow({ user, text }) {
  const { t } = useI18n();
  return (
    <div className="calc-save">
      <span className="calc-save-text">
        <strong>{t('Save this to your journal')}</strong>
        <span>{text}</span>
      </span>
      <Link to={user ? '/app' : '/signup'} className="calc-save-button">{user ? t('Open journal') : t('Start your free journal')}</Link>
    </div>
  );
}

// The answer card: `tone` good (green), bad (red, "Not yet") or wait (nothing to show yet).
function Answer({ tone, badge, title, text, children, page, user, save }) {
  const { t } = useI18n();
  return (
    <section id="calc-answer" className={`calc-answer calc-${tone}`} aria-live="polite">
      <span className="calc-badge"><span className="calc-dot" aria-hidden="true" /> {badge || t('Your answer')}</span>
      {title && <h2 className="calc-headline">{title}</h2>}
      {text && <p className="calc-sentence">{text}</p>}
      {children}
      {page && save && <SaveRow user={user} text={save} />}
    </section>
  );
}

function HowItWorks({ steps }) {
  const { t } = useI18n();
  return (
    <section className="calc-how">
      <h2>{t('How it works')}</h2>
      <ol>
        {steps.map(([title, text], i) => (
          <li key={title}>
            <strong>{i + 1}. {title}</strong>
            <p>{text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// Phones: the short answer stays at the bottom of the screen while the trader types.
function PhoneBar({ tone, title, sub }) {
  const { t } = useI18n();
  if (!title) return null;
  return (
    <div className={`calc-phone-bar calc-${tone}`}>
      <span className="calc-dot" aria-hidden="true" />
      <span className="calc-phone-text">
        <strong>{title}</strong>
        {sub && <span>{sub}</span>}
      </span>
      <button type="button" onClick={() => document.getElementById('calc-answer')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{t('Details')}</button>
    </div>
  );
}

// `page`: on the public calculator page (example values, how it works, the phone bar and the
// sign-up line); without it, the calculator alone (the position size window on the Live session).
function CalcLayout({ page, inputs, answer, guide, how, bar }) {
  return (
    <div className={`calc${page ? ' calc-page' : ''}`}>
      <div className="calc-grid">
        <div className="calc-inputs">{inputs}</div>
        {answer}
      </div>
      {page && guide}
      {page && how}
      {page && bar}
    </div>
  );
}

// --- Position size ---------------------------------------------------------------

const SETTINGS_KEY = 'mm-position-calc';
const POSITION_EXAMPLE = { symbol: 'MNQ', side: 'long', mode: 'prices', stopPts: '20', targetPts: '40', riskMode: 'dollars', risk: '200', drawdown: '2000', riskPct: '10', commission: '' };
const PRICE_EXAMPLE = { entry: '21500', stop: '21480', target: '21540' };
const NO_PRICES = { entry: '', stop: '', target: '' };

// The calculator's settings from last time (not prices, which change every trade).
function savedSettings() {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY)) || {};
  } catch {
    return {};
  }
}

function startPosition(page) {
  const saved = savedSettings();
  const v = { ...POSITION_EXAMPLE };
  for (const k of Object.keys(POSITION_EXAMPLE)) if (saved[k] !== undefined && saved[k] !== null) v[k] = saved[k];
  if (!CONTRACTS.some((x) => x.symbol === v.symbol)) v.symbol = 'MNQ';
  // The example prices are Nasdaq prices for a long trade, so they only fill in for those.
  const prices = page && ['MNQ', 'NQ'].includes(v.symbol) && v.side === 'long' ? PRICE_EXAMPLE : NO_PRICES;
  return { ...v, ...prices };
}

// Stop on the left in red, target on the right in green, entry where they meet.
function RiskBar({ risk, reward, left, mid, right, riskLabel, rewardLabel }) {
  const { t } = useI18n();
  const share = reward > 0 ? risk / (risk + reward) : 1;
  return (
    <div className="calc-rr">
      <span className="calc-section-label">{t('Risk vs reward')}</span>
      <div className="calc-rr-top">
        <span className="loss">{riskLabel}</span>
        <span className="calc-rr-entry" style={{ left: `${share * 100}%` }}>{t('Entry')}</span>
        {reward > 0 && <span className="gain">{rewardLabel}</span>}
      </div>
      <div className="calc-rr-bar">
        <span className="calc-rr-risk" style={{ width: `${share * 100}%` }} />
        {reward > 0 && <span className="calc-rr-reward" />}
        <span className="calc-rr-mark" style={{ left: `${share * 100}%` }} />
      </div>
      <div className="calc-rr-bottom">
        <span>{left}</span>
        <span className="calc-rr-entry" style={{ left: `${share * 100}%` }}>{mid}</span>
        <span>{right}</span>
      </div>
    </div>
  );
}

export function PositionSize({ user, page, guide }) {
  const { t, tr } = useI18n();
  const [v, set, setV] = useValues(() => startPosition(page));
  const { symbol, side, mode, entry, stop, target, stopPts, targetPts, riskMode, risk, drawdown, riskPct, commission } = v;

  // Remember the settings for next time, so only the prices need typing.
  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({ symbol, side, mode, stopPts, targetPts, riskMode, risk, drawdown, riskPct, commission }));
    } catch {
      // Not remembered in private mode.
    }
  }, [symbol, side, mode, stopPts, targetPts, riskMode, risk, drawdown, riskPct, commission]);
  const c = findContract(symbol);
  const touched = !sameAs(v, { ...POSITION_EXAMPLE, ...PRICE_EXAMPLE });

  // Distances in points from the prices or straight from the inputs.
  let stopDist = null;
  let targetDist = null;
  let problem = '';
  if (mode === 'prices') {
    const e = num(entry);
    const s = num(stop);
    const tp = num(target);
    if (e !== null && s !== null) {
      stopDist = side === 'long' ? e - s : s - e;
      if (stopDist <= 0) problem = side === 'long' ? t('For a long trade, your stop loss goes below your entry.') : t('For a short trade, your stop loss goes above your entry.');
    }
    if (e !== null && tp !== null) {
      targetDist = side === 'long' ? tp - e : e - tp;
      if (targetDist <= 0 && !problem) problem = side === 'long' ? t('For a long trade, your take profit goes above your entry.') : t('For a short trade, your take profit goes below your entry.');
    }
  } else {
    stopDist = num(stopPts);
    targetDist = num(targetPts);
  }
  const riskBudget = riskMode === 'dollars' ? num(risk) : (num(drawdown) !== null && num(riskPct) !== null ? (num(drawdown) * num(riskPct)) / 100 : null);
  const fee = num(commission) || 0;
  const ready = !problem && stopDist > 0 && riskBudget > 0;
  const stopTicks = ready ? toTicks(stopDist, c) : 0;
  const riskPer = ready ? stopTicks * c.tick * c.point + fee : 0;
  const contracts = ready ? Math.floor(riskBudget / riskPer) : 0;
  const reward = ready && targetDist > 0 ? toTicks(targetDist, c) * c.tick * c.point - fee : null;
  const rr = reward !== null && riskPer > 0 ? reward / riskPer : null;
  const micro = c.micro ? findContract(c.micro) : null;
  const microRisk = micro && ready ? stopTicks * c.tick * micro.point + fee : null;
  const microContracts = microRisk ? Math.floor(riskBudget / microRisk) : 0;
  const totalRisk = riskPer * contracts;
  const totalReward = reward !== null ? reward * contracts : null;
  const stopPoints = fmt(stopTicks * c.tick, 4);

  const inputs = (
    <div className="tool-card calc-card">
      {page && <ExampleLine touched={touched} onReset={() => setV({ ...POSITION_EXAMPLE, ...PRICE_EXAMPLE })} />}
      <ContractChips value={symbol} onChange={set('symbol')} />
      <div className="tool-row">
        <div className="tool-field">
          <span className="tool-label">{t('Direction')}</span>
          <Toggle value={side} onChange={set('side')} label={t('Direction')} options={[['long', t('Long'), 'gain'], ['short', t('Short'), 'loss']]} />
        </div>
        <div className="tool-field">
          <span className="tool-label">{t('Enter stop as')}</span>
          <Toggle value={mode} onChange={set('mode')} label={t('Enter stop as')} options={[['prices', t('Prices')], ['points', t('Points')]]} />
        </div>
      </div>
      {mode === 'prices' ? (
        <div className="tool-row three">
          <Field label={t('Entry')}><NumberInput value={entry} onChange={set('entry')} placeholder="21,500" /></Field>
          <Field label={t('Stop loss')}><NumberInput value={stop} onChange={set('stop')} placeholder={side === 'long' ? '21,480' : '21,520'} /></Field>
          <Field label={t('Target')} optional><NumberInput value={target} onChange={set('target')} placeholder={side === 'long' ? '21,540' : '21,460'} /></Field>
        </div>
      ) : (
        <div className="tool-row">
          <Field label={t('Stop loss')}><NumberInput value={stopPts} onChange={set('stopPts')} placeholder="20" suffix={t('pts')} /></Field>
          <Field label={t('Target')} optional><NumberInput value={targetPts} onChange={set('targetPts')} placeholder="40" suffix={t('pts')} /></Field>
        </div>
      )}
      <div className="tool-field">
        <span className="calc-label-row">
          <span className="tool-label">{t('Max risk per trade')}</span>
          <Toggle small value={riskMode} onChange={set('riskMode')} label={t('Risk')} options={[['dollars', t('$ amount')], ['percent', t('% of drawdown')]]} />
        </span>
        {riskMode === 'dollars' ? (
          <>
            <NumberInput value={risk} onChange={set('risk')} prefix="$" placeholder="200" />
            <div className="calc-chips small">
              {['100', '200', '500'].map((x) => (
                <button key={x} type="button" className={`calc-chip${String(risk) === x ? ' on' : ''}`} aria-pressed={String(risk) === x} onClick={() => set('risk')(x)}>${x}</button>
              ))}
            </div>
            <span className="field-hint">{t('Many prop traders risk 10–25% of their daily loss limit per trade.')}</span>
          </>
        ) : (
          <div className="tool-row">
            <Field label={t('Drawdown left ($)')} hint={t('Your distance to the max loss level.')}><NumberInput value={drawdown} onChange={set('drawdown')} prefix="$" placeholder="2000" /></Field>
            <Field label={t('Risk per trade (%)')}><NumberInput value={riskPct} onChange={set('riskPct')} placeholder="10" suffix="%" /></Field>
          </div>
        )}
      </div>
      <MoreOptions note={t('commission')}>
        <Field label={t('Commission per contract, round trip ($)')} hint={t('Optional. Added to the risk and taken off the reward.')}>
          <NumberInput value={commission} onChange={set('commission')} prefix="$" placeholder="0.00" />
        </Field>
      </MoreOptions>
    </div>
  );

  const contractsTitle = (n) => tr(n === 1 ? 'Trade <0>{n}</0> {symbol} contract' : 'Trade <0>{n}</0> {symbol} contracts', [(x) => <span className="calc-num">{x}</span>], { n, symbol: c.symbol });
  let answer;
  if (problem) {
    answer = <Answer tone="bad" badge={t('Check your prices')} text={problem} />;
  } else if (!ready) {
    answer = <Answer tone="wait" text={t('Enter your stop loss and how much you want to risk to see your position size.')} />;
  } else {
    const microTip = micro && microContracts > 0 && (
      <p className="tool-tip">
        {tr('Same trade on the micro: <0>{n} {symbol}</0> at {amount} risk each ({total} total). Micros let you size much more precisely.',
          [(x) => <strong>{x}</strong>], { n: microContracts, symbol: micro.symbol, amount: money(microRisk), total: money(microRisk * microContracts) })}
      </p>
    );
    answer = contracts > 0 ? (
      <Answer tone="good" title={contractsTitle(contracts)} page={page} user={user} save={t('Log the trade and track if you stick to your size.')}
        text={totalReward !== null
          ? tr('A full stop-out costs <0>{risk}</0> of your {max} max, and hitting your target makes <0>{reward}</0>.', [(x) => <strong>{x}</strong>], { risk: cash(totalRisk), max: cash(riskBudget), reward: cash(totalReward) })
          : tr('A full stop-out costs <0>{risk}</0> of your {max} max.', [(x) => <strong>{x}</strong>], { risk: cash(totalRisk), max: cash(riskBudget) })}>
        <Tiles items={[
          [cash(totalRisk), t('Dollar risk'), 'loss'],
          totalReward !== null ? [cash(totalReward), t('Dollar reward'), 'gain'] : [cash(riskPer), t('Risk per contract'), ''],
          rr !== null ? [`1 : ${fmt(rr, 2)}`, t('Risk : reward'), rr < 1 ? 'loss' : 'cyan'] : null,
        ]} />
        <RiskBar risk={stopTicks} reward={reward !== null ? toTicks(targetDist, c) : 0}
          riskLabel={`${t('Stop')} −${cash(totalRisk)}`} rewardLabel={totalReward !== null ? `${t('Target')} +${cash(totalReward)}` : ''}
          left={mode === 'prices' ? entry && stop ? fmt(num(stop), 4) : '' : `−${stopPoints} ${t('pts')}`}
          mid={mode === 'prices' ? fmt(num(entry), 4) : ''}
          right={reward !== null ? (mode === 'prices' ? fmt(num(target), 4) : `+${fmt(toTicks(targetDist, c) * c.tick, 4)} ${t('pts')}`) : ''} />
        <p className="calc-small">{t('{points} pts stop ({n} ticks) · {amount} risk per contract', { points: stopPoints, n: stopTicks, amount: money(riskPer) })}</p>
        {microTip}
        {rr !== null && rr < 1 && <p className="tool-warning">{t('Your target is closer than your stop. You need to win more than half of these trades just to break even. Consider skipping it.')}</p>}
      </Answer>
    ) : (
      <Answer tone="bad" badge={t('Too big')} title={t('Even 1 {symbol} is over your limit', { symbol: c.symbol })} page={page} user={user} save={t('Log the trade and track if you stick to your size.')}
        text={tr('Even <0>1 {symbol}</0> risks {amount}, more than your {budget} limit. Don’t force it: widen your risk budget only if your plan allows it, wait for a setup with a closer stop, or trade the micro.',
          [(x) => <strong>{x}</strong>], { symbol: c.symbol, amount: money(riskPer), budget: money(riskBudget) })}>
        {microTip}
      </Answer>
    );
  }

  const how = (
    <HowItWorks steps={[
      [t('Risk per contract'), `${t('Stop distance in points × the contract’s dollar value per point, plus commission.')}${ready ? ` ${t('{points} pts on {symbol} ({point}) = {amount}.', { points: stopPoints, symbol: c.symbol, point: perPoint(c.point), amount: cash(riskPer) })}` : ''}`],
      [t('Contracts'), `${t('Your max risk ÷ risk per contract, rounded down so you never risk more than you planned.')}${ready ? ` ${cash(riskBudget)} ÷ ${cash(riskPer)} = ${contracts}.` : ''}`],
      [t('Reward and R:R'), t('Target distance × point value × contracts, minus commission. R:R compares that reward to the dollars at risk.')],
    ]} />
  );
  const bar = ready && (
    <PhoneBar tone={contracts > 0 ? 'good' : 'bad'}
      title={contracts > 0 ? t(contracts === 1 ? 'Trade {n} {symbol} contract' : 'Trade {n} {symbol} contracts', { n: contracts, symbol: c.symbol }) : t('Even 1 {symbol} is over your limit', { symbol: c.symbol })}
      sub={contracts > 0 ? [`${t('Risk')} ${cash(totalRisk)}`, totalReward !== null && `${t('Reward')} ${cash(totalReward)}`, rr !== null && `1:${fmt(rr, 1)}`].filter(Boolean).join(' · ') : ''} />
  );
  return <CalcLayout page={page} inputs={inputs} answer={answer} guide={guide} how={how} bar={bar} />;
}

// --- Drawdown: max contracts for a losing streak -------------------------------------

const DRAWDOWN_EXAMPLE = { symbol: 'MNQ', stopPts: '20', drawdown: '2000', daily: '1000', streak: '4', commission: '' };

function Drawdown({ user, page, guide }) {
  const { t, tr } = useI18n();
  const [v, set, setV] = useValues(DRAWDOWN_EXAMPLE);
  const [table, setTable] = useState(false);
  const c = findContract(v.symbol);
  const stopDist = num(v.stopPts);
  const dd = num(v.drawdown);
  const dl = num(v.daily);
  const n = Math.max(1, Math.round(num(v.streak) || 1));
  const fee = num(v.commission) || 0;
  const ready = stopDist > 0 && dd > 0;
  const riskPer = ready ? toTicks(stopDist, c) * c.tick * c.point + fee : 0;
  const limit = dl > 0 ? Math.min(dd, dl) : dd;
  const maxContracts = ready ? Math.floor(limit / (n * riskPer)) : 0;
  const rows = ready ? [1, 2, 3, 4, 5, 6, 8, 10].map((k) => ({
    k,
    risk: k * riskPer,
    daily: dl > 0 ? Math.floor(dl / (k * riskPer)) : null,
    total: Math.floor(dd / (k * riskPer)),
  })) : [];
  // Bars: the stop-outs each size can take before the tighter of the two limits.
  const scale = Math.max(10, Math.ceil(n * 2.5));
  const bars = ready ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((k) => {
    const can = Math.floor(limit / (k * riskPer));
    return {
      key: k, name: t('{n} ct', { n: k }), share: can / scale, best: k === maxContracts,
      tone: can >= n ? 'gain' : 'loss', value: t(can === 1 ? '{n} stop-out' : '{n} stop-outs', { n: can }),
    };
  }) : [];

  const inputs = (
    <div className="tool-card calc-card">
      {page && <ExampleLine touched={!sameAs(v, DRAWDOWN_EXAMPLE)} onReset={() => setV(DRAWDOWN_EXAMPLE)} />}
      <ContractChips value={v.symbol} onChange={set('symbol')} />
      <div className="tool-row">
        <Field label={t('Usual stop')}><NumberInput value={v.stopPts} onChange={set('stopPts')} placeholder="20" suffix={t('pts')} /></Field>
        <Field label={t('Losses in a row to survive')}><NumberInput value={v.streak} onChange={set('streak')} placeholder="4" suffix={t('trades')} /></Field>
      </div>
      <div className="tool-row">
        <Field label={t('Drawdown left')} hint={t('Your balance minus your max loss level.')}><NumberInput value={v.drawdown} onChange={set('drawdown')} prefix="$" placeholder="2000" /></Field>
        <Field label={t('Daily loss limit')} optional><NumberInput value={v.daily} onChange={set('daily')} prefix="$" placeholder="1000" /></Field>
      </div>
      <MoreOptions note={t('commission')}>
        <Field label={t('Commission per contract, round trip ($)')} hint={t('Optional')}>
          <NumberInput value={v.commission} onChange={set('commission')} prefix="$" placeholder="0.00" />
        </Field>
      </MoreOptions>
    </div>
  );

  const limitName = dl > 0 && dl <= dd ? t('{amount} daily loss limit', { amount: cash(limit) }) : t('{amount} drawdown left', { amount: cash(limit) });
  const answer = !ready ? (
    <Answer tone="wait" text={t('Enter your stop loss and drawdown to see how many contracts you can trade.')} />
  ) : (
    <Answer tone={maxContracts ? 'good' : 'bad'} badge={maxContracts ? '' : t('Not yet')} page={page} user={user} save={t('Track your drawdown left after every session.')}
      title={maxContracts
        ? tr(maxContracts === 1 ? 'Trade up to <0>{n}</0> {symbol} contract' : 'Trade up to <0>{n}</0> {symbol} contracts', [(x) => <span className="calc-num">{x}</span>], { n: maxContracts, symbol: c.symbol })
        : t('Even 1 contract is too big')}
      text={maxContracts
        ? tr('At {n} contracts each full stop-out costs <0>{amount}</0>. {streak} in a row costs <0>{total}</0>, inside your {limit}.', [(x) => <strong>{x}</strong>],
          { n: maxContracts, amount: cash(maxContracts * riskPer), streak: n, total: cash(n * maxContracts * riskPer), limit: limitName })
        : t('Even 1 contract can’t survive {n} full stop-outs inside your {limit} limit. Use a smaller contract, a closer stop, or plan for fewer losses before you stop for the day.', { n, limit: money(limit) })}>
      <Bars label={t('Stop-outs each size can take')} rows={bars} marker={n / scale}
        legend={<><span className="calc-key gold" /> {t('Losses you want to survive')} <span className="calc-key green" /> {t('Recommended size')}</>} />
      <button type="button" className="calc-link" aria-expanded={table} onClick={() => setTable(!table)}>{table ? t('Hide the table') : t('Show full table')}</button>
      {table && (
        <div className="table-wrap tool-table">
          <table>
            <thead>
              <tr>
                <th scope="col">{t('Contracts')}</th>
                <th scope="col">{t('Risk per trade')}</th>
                {dl > 0 && <th scope="col">{t('Losses to daily limit')}</th>}
                <th scope="col">{t('Losses to max loss')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.k} className={r.k === maxContracts ? 'tool-row-best' : ''}>
                  <th scope="row">{r.k}</th>
                  <td>{money(r.risk)}</td>
                  {dl > 0 && <td className={r.daily < n ? 'loss' : ''}>{r.daily}</td>}
                  <td className={r.total < n ? 'loss' : ''}>{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Answer>
  );

  const how = (
    <HowItWorks steps={[
      [t('Cost of one stop-out'), `${t('Stop points × point value × contracts, plus commission.')}${ready ? ` ${t('{points} pts on {symbol} = {amount} per contract.', { points: fmt(stopDist, 4), symbol: c.symbol, amount: cash(riskPer) })}` : ''}`],
      [t('Your streak'), t('Multiply by the losses in a row you want to survive. That total must fit inside your daily loss limit and your drawdown left.')],
      [t('The answer'), t('The biggest size where the whole streak still fits. Anything above it can end the day or the account early.')],
    ]} />
  );
  const bar = ready && (
    <PhoneBar tone={maxContracts ? 'good' : 'bad'}
      title={maxContracts ? t(maxContracts === 1 ? 'Trade up to {n} {symbol} contract' : 'Trade up to {n} {symbol} contracts', { n: maxContracts, symbol: c.symbol }) : t('Even 1 contract is too big')}
      sub={maxContracts ? t('{streak} stop-outs in a row cost {total}', { streak: n, total: cash(n * maxContracts * riskPer) }) : ''} />
  );
  return <CalcLayout page={page} inputs={inputs} answer={answer} guide={guide} how={how} bar={bar} />;
}

// --- Consistency rule -------------------------------------------------------------------

// One preset per firm and plan that has a consistency rule.
const CONSISTENCY_PRESETS = [];
for (const p of PRESETS) {
  if (!p.consistencyLimit) continue;
  const plan = p.label.replace(/\s+\d+K\b/, '').replace('Combine', 'evaluation').replace(/^(\S+) Static evaluation$/, '$1 evaluation');
  if (!CONSISTENCY_PRESETS.some((x) => x.label === plan)) {
    CONSISTENCY_PRESETS.push({ label: plan, limit: p.consistencyLimit, base: p.consistencyBase });
  }
}

const CONSISTENCY_EXAMPLE = { preset: '', limit: '50', base: 'total', best: '900', total: '1500', target: '3000' };

function Consistency({ user, page, guide }) {
  const { t, tr } = useI18n();
  const [v, set, setV] = useValues(CONSISTENCY_EXAMPLE);
  const { preset, limit, base, best, total, target } = v;

  function applyPreset(label) {
    const p = CONSISTENCY_PRESETS.find((x) => x.label === label);
    setV((o) => (p ? { ...o, preset: label, limit: String(p.limit), base: p.base || 'total' } : { ...o, preset: label }));
  }

  const L = num(limit);
  const b = num(best);
  const tot = num(total);
  const tgt = num(target);
  let out = null;
  if (L > 0 && b > 0 && base === 'total' && tot > 0) {
    const pct = (b / tot) * 100;
    const needed = b / (L / 100);
    out = { pct, ok: pct <= L, needed, more: Math.max(0, needed - tot), maxDay: (tot * L) / 100 };
  } else if (L > 0 && b > 0 && base === 'target' && tgt > 0) {
    const pct = (b / tgt) * 100;
    out = { pct, ok: pct <= L, maxDay: (tgt * L) / 100 };
  }
  const onTotal = base === 'total';
  const pctText = out ? `${fmt(out.pct, 1)}%` : '';

  const inputs = (
    <div className="tool-card calc-card">
      {page && <ExampleLine touched={!sameAs(v, CONSISTENCY_EXAMPLE)} onReset={() => setV(CONSISTENCY_EXAMPLE)} />}
      <Field label={t('Firm preset')} hint={t('Rules change often: check the numbers against your firm’s current rules.')}>
        <select value={preset} onChange={(e) => applyPreset(e.target.value)}>
          <option value="">{t('Custom rule')}</option>
          {CONSISTENCY_PRESETS.map((p) => <option key={p.label} value={p.label}>{p.label} ({p.limit}%)</option>)}
        </select>
      </Field>
      <Field label={t('Consistency limit')}>
        <NumberInput value={limit} onChange={(x) => setV((o) => ({ ...o, limit: x, preset: '' }))} placeholder="50" suffix={t('% max for best day')} wide />
      </Field>
      <div className="tool-row">
        <Field label={t('Best day')}><NumberInput value={best} onChange={set('best')} prefix="$" placeholder="900" /></Field>
        {onTotal ? (
          <Field label={t('Total profit so far')}><NumberInput value={total} onChange={set('total')} prefix="$" placeholder="1500" /></Field>
        ) : (
          <Field label={t('Profit target')}><NumberInput value={target} onChange={set('target')} prefix="$" placeholder="3000" /></Field>
        )}
      </div>
      <MoreOptions note={t('rule based on profit target')}>
        <div className="tool-field">
          <span className="tool-label">{t('Consistency based on')}</span>
          <Toggle value={base} onChange={(x) => setV((o) => ({ ...o, base: x, preset: '' }))} label={t('Consistency based on')}
            options={[['total', t('Total profit')], ['target', t('Profit target')]]} />
        </div>
      </MoreOptions>
    </div>
  );

  const strong = [(x) => <strong>{x}</strong>];
  let answer;
  if (!out) {
    answer = <Answer tone="wait" text={t('Enter your best day and your total profit to check the rule.')} />;
  } else {
    const vars = { best: cash(b), pct: pctText, total: cash(tot || 0), target: cash(tgt || 0), limit: L, needed: cash(out.needed || 0), max: cash(out.maxDay) };
    const title = out.ok
      ? t('You’re within the rule')
      : onTotal ? tr('You need <0>{amount}</0> more', [(x) => <span className="calc-num">{x}</span>], { amount: cash(out.more) }) : t('Over the limit');
    const text = onTotal
      ? (out.ok
        ? tr('Your best day (<0>{best}</0>) is {pct} of your total profit (<0>{total}</0>). The limit is {limit}%, so keep each day under <0>{max}</0> at your current total.', strong, vars)
        : tr('Your best day (<0>{best}</0>) is {pct} of your total profit (<0>{total}</0>). The limit is {limit}%, so total profit needs to reach <0>{needed}</0> without a bigger best day.', strong, vars))
      : (out.ok
        ? tr('Your best day (<0>{best}</0>) is {pct} of your profit target (<0>{target}</0>). Keep each day under <0>{max}</0>.', strong, vars)
        : t('This day is over the limit. Check your firm’s rules for what happens next; some reset the day or require more trading days.'));
    answer = (
      <Answer tone={out.ok ? 'good' : 'bad'} badge={out.ok ? t('Within the rule') : t('Not yet')} title={title} text={text} page={page} user={user}
        save={t('Market Matrix tracks your best-day share automatically.')}>
        <Tiles items={onTotal ? [
          [pctText, t('Best day share'), out.ok ? 'gain' : 'loss'],
          [cash(out.maxDay), t('Allowed best day now'), ''],
          [cash(out.needed), t('Total profit needed'), 'cyan'],
        ] : [
          [pctText, t('Best day share'), out.ok ? 'gain' : 'loss'],
          [cash(out.maxDay), t('Biggest day allowed'), 'cyan'],
        ]} />
        <div className="calc-share">
          <span className="calc-section-label">{onTotal ? t('Best day share of total profit') : t('Best day share of profit target')}</span>
          <div className="calc-share-top"><span className="calc-share-limit" style={{ left: `${Math.min(100, L)}%` }}>{t('Limit {limit}%', { limit: L })}</span></div>
          <div className="calc-share-bar">
            <span className={`calc-share-fill ${out.ok ? 'gain' : 'loss'}`} style={{ width: `${Math.min(100, out.pct)}%` }} />
            <span className="calc-share-mark" style={{ left: `${Math.min(100, L)}%` }} />
          </div>
          <div className="calc-share-bottom">
            <span>0%</span>
            <span className="calc-share-you" style={{ left: `${Math.min(100, out.pct)}%` }}>{t('You {pct}', { pct: pctText })}</span>
            <span>100%</span>
          </div>
        </div>
      </Answer>
    );
  }

  const base2 = onTotal ? cash(tot || 0) : cash(tgt || 0);
  const how = (
    <HowItWorks steps={[
      [t('The rule'), t('Your best single day can’t be more than a set % of your total profit (or of the profit target, depending on the firm).')],
      [t('The check'), out
        ? t(out.ok ? '{best} ÷ {base} = {pct}, inside a {limit}% limit.' : '{best} ÷ {base} = {pct}, above a {limit}% limit.', { best: cash(b), base: base2, pct: pctText, limit: L })
        : t('Best day ÷ total profit, compared with the limit.')],
      [t('How to pass'), out && onTotal
        ? (out.more > 0
          ? t('Total profit must reach best day ÷ limit: {best} ÷ {limit}% = {needed}, so you need {more} more without a bigger best day.', { best: cash(b), limit: L, needed: cash(out.needed), more: cash(out.more) })
          : t('Total profit must reach best day ÷ limit: {best} ÷ {limit}% = {needed}, and you’re already past it.', { best: cash(b), limit: L, needed: cash(out.needed) }))
        : out ? t('Keep every day under {max} ({limit}% of {target}).', { max: cash(out.maxDay), limit: L, target: cash(tgt) })
          : t('Total profit must reach best day ÷ limit, without a bigger best day.')],
    ]} />
  );
  const bar = out && (
    <PhoneBar tone={out.ok ? 'good' : 'bad'}
      title={out.ok ? t('You’re within the rule') : onTotal ? t('You need {amount} more', { amount: cash(out.more) }) : t('Over the limit')}
      sub={t('Best day {pct} · limit {limit}%', { pct: pctText, limit: L })} />
  );
  return <CalcLayout page={page} inputs={inputs} answer={answer} guide={guide} how={how} bar={bar} />;
}

// --- Risk plan: a daily max loss that survives a long losing streak -------------------
// Two ways round: check a daily max loss ("how many losing days until it's blown?"),
// or find one ("I want it to take 100 losing days: how much can I lose a day?").
// "Blown" means the balance reaches zero, or the floor the trader sets.

// The balance after each losing day as an area line, with the goal as a gold dashed line.
function BalanceChart({ after, start, floor, days, goal }) {
  const { t } = useI18n();
  const W = 320;
  const H = 120;
  const step = Math.max(1, Math.ceil(days / 60));
  const ks = [];
  for (let k = 0; k <= days; k += step) ks.push(k);
  if (ks[ks.length - 1] !== days) ks.push(days);
  const x = (k) => (k / days) * W;
  const y = (bal) => H - (Math.max(0, bal) / start) * H;
  const line = ks.map((k, i) => `${i ? 'L' : 'M'}${x(k).toFixed(1)},${y(after(k)).toFixed(1)}`).join(' ');
  const gx = x(Math.min(goal, days));
  const gy = y(after(goal));
  const short = (n) => (n >= 1000 ? `$${fmt(n / 1000, 1)}k` : cash(n));
  return (
    <div className="calc-chart">
      <span className="calc-section-label">{t('Balance across losing days')}</span>
      <div className="calc-chart-box">
        <span className="calc-chart-y"><span>{short(start)}</span><span>{short(floor)}</span></span>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={t('Balance across losing days')}>
          <defs>
            <linearGradient id="calc-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2fe39a" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#2fe39a" stopOpacity="0" />
            </linearGradient>
          </defs>
          {floor > 0 && <line x1="0" x2={W} y1={y(floor)} y2={y(floor)} className="calc-chart-floor" />}
          <path d={`${line} L${W},${H} L0,${H} Z`} fill="url(#calc-area)" />
          <path d={line} className="calc-chart-line" vectorEffect="non-scaling-stroke" />
          <line x1={gx} x2={gx} y1="0" y2={H} className="calc-chart-goal" vectorEffect="non-scaling-stroke" />
        </svg>
        <span className="calc-chart-dot" style={{ left: `${(gx / W) * 100}%`, top: `${(gy / H) * 100}%` }} />
        <span className="calc-chart-goal-label" style={{ left: `${(gx / W) * 100}%` }}>{t('Goal: {n} days', { n: goal })}</span>
      </div>
      <div className="calc-chart-x"><span>0</span><span>{Math.round(days / 2)}</span><span>{t('{n} days', { n: days })}</span></div>
    </div>
  );
}

const RISK_EXAMPLE = { mode: 'check', balance: '5000', unit: 'percent', daily: '1', basis: 'start', streak: '50', floor: '', trades: '3', symbol: 'MNQ', stopPts: '' };

function RiskPlan({ user, page, guide }) {
  const { t, tr } = useI18n();
  const [v, set, setV] = useValues(RISK_EXAMPLE);
  const [table, setTable] = useState(false);
  const { mode, balance, unit, daily, basis, streak, floor, trades, symbol, stopPts } = v;

  const B = num(balance);
  const F = Math.max(0, num(floor) || 0);
  const N = Math.max(1, Math.round(num(streak) || 50));
  const perDay = Math.max(1, Math.round(num(trades) || 1));
  const value = num(daily);
  const finding = mode === 'find';
  const valid = B > 0 && F < B;

  // The daily max loss the trader checks, or the one that takes exactly N losing days to blow the account.
  const fixedForGoal = valid ? (B - F) / N : 0;
  const keep = F > 0 ? F / B : 0.5;
  const shrinkPctForGoal = (1 - keep ** (1 / N)) * 100;
  const shrinking = !finding && unit === 'percent' && basis === 'current';
  const p = finding ? fixedForGoal / B : unit === 'percent' ? (value || 0) / 100 : (value || 0) / B;
  const ready = valid && (finding || (value > 0 && (unit === 'dollars' || value < 100)));
  const todayLoss = ready ? (finding ? fixedForGoal : B * p) : 0;

  let daysToBlow = 0;
  let after = () => 0;
  if (ready && shrinking) {
    // Each losing day takes p of what's left, so without a floor it never quite reaches zero.
    daysToBlow = F > 0 ? Math.ceil(Math.log(F / B) / Math.log(1 - p) - 1e-9) : Infinity;
    after = (k) => B * (1 - p) ** k;
  } else if (ready) {
    daysToBlow = Math.ceil((B - F) / todayLoss - 1e-9);
    after = (k) => Math.max(0, B - k * todayLoss);
  }
  const ok = daysToBlow >= N;
  const perTrade = todayLoss / perDay;
  const c = findContract(symbol);
  const stopDist = num(stopPts);
  const riskPer = stopDist > 0 ? toTicks(stopDist, c) * c.tick * c.point : 0;
  const contracts = riskPer && ready ? Math.floor(perTrade / riskPer) : null;
  const milestones = [...new Set([5, 10, 20, 30, 40, 50, 75, 100, N])].filter((k) => k <= Math.max(N, 50)).sort((a, b) => a - b);
  const blownLabel = F > 0 ? t('to reach your floor of {amount}', { amount: money(F) }) : t('to blow the account');
  const endLabel = F > 0 ? t('before the account reaches your floor of {amount}', { amount: cash(F) }) : t('before the account hits zero');
  // The chart runs to the day the account is blown (or twice the goal when it never is).
  const chartDays = Number.isFinite(daysToBlow) && daysToBlow > 0 ? Math.max(N, Math.min(daysToBlow, N * 3)) : N * 2;

  const inputs = (
    <div className="tool-card calc-card">
      {page && <ExampleLine touched={!sameAs(v, RISK_EXAMPLE)} onReset={() => setV(RISK_EXAMPLE)} />}
      <Toggle value={mode} onChange={set('mode')} label={t('What do you want to do?')}
        options={[['check', t('Check my daily max loss')], ['find', t('Find my daily max loss')]]} />
      <div className="tool-field">
        <span className="tool-label">{t('Starting balance')}</span>
        <NumberInput value={balance} onChange={set('balance')} prefix="$" placeholder="5000" />
        <div className="calc-chips small">
          {[['5000', '$5k'], ['25000', '$25k'], ['50000', '$50k'], ['100000', '$100k']].map(([x, label]) => (
            <button key={x} type="button" className={`calc-chip${String(balance) === x ? ' on' : ''}`} aria-pressed={String(balance) === x} onClick={() => set('balance')(x)}>{label}</button>
          ))}
        </div>
      </div>
      {!finding && (
        <div className="tool-field">
          <span className="calc-label-row">
            <span className="tool-label">{t('Daily max loss')}</span>
            <Toggle small value={unit} onChange={set('unit')} label={t('Daily max loss')} options={[['percent', '%'], ['dollars', '$']]} />
          </span>
          <NumberInput value={daily} onChange={set('daily')} prefix={unit === 'dollars' ? '$' : ''} wide={unit === 'percent'}
            suffix={unit === 'percent' ? (basis === 'current' ? t('% of current balance') : t('% of balance')) : ''} placeholder={unit === 'percent' ? '1' : '50'} />
        </div>
      )}
      <Field label={finding ? t('Losing days in a row it should take to blow the account') : t('Losing days in a row you want to survive')}
        hint={finding ? t('Your worst-case streak. More days means a smaller daily max loss.') : ''}>
        <NumberInput value={streak} onChange={set('streak')} placeholder="50" suffix={t('days')} />
      </Field>
      <MoreOptions note={t('balance basis, floor, trades per day, contract')}>
        {!finding && unit === 'percent' && (
          <div className="tool-field">
            <span className="tool-label">{t('Percent of')}</span>
            <Toggle value={basis} onChange={set('basis')} label={t('Percent of')} options={[['start', t('Starting balance')], ['current', t('Current balance')]]} />
            <span className="field-hint">{basis === 'start' ? t('The same dollar limit every day, based on your starting balance.') : t('Recalculated each day, so the limit shrinks as your account shrinks.')}</span>
          </div>
        )}
        <Field label={t('Never let the account fall below ($)')} hint={t('Optional. Counts reaching this amount as blown, so you keep some money.')}>
          <NumberInput value={floor} onChange={set('floor')} prefix="$" placeholder="0" />
        </Field>
        <Field label={t('Max trades per day')} hint={t('Splits your daily max loss into a risk per trade.')}>
          <NumberInput value={trades} onChange={set('trades')} placeholder="3" suffix={t('trades')} />
        </Field>
        <ContractChips value={symbol} onChange={set('symbol')} />
        <Field label={t('Your usual stop loss (points)')} hint={t('Optional. Shows how many contracts fit.')}>
          <NumberInput value={stopPts} onChange={set('stopPts')} placeholder="20" suffix={t('pts')} />
        </Field>
      </MoreOptions>
    </div>
  );

  const num0 = [(x) => <span className="calc-num">{x}</span>];
  const strong = [(x) => <strong>{x}</strong>];
  const lostPct = ready ? fmt(((B - after(N)) / B) * 100, 0) : 0;
  const tiles = ready && (
    <Tiles items={[
      [cash(todayLoss), shrinking ? t('Daily max loss today') : t('Daily max loss'), 'loss'],
      [cash(perTrade), t('Risk per trade ({n}/day)', { n: perDay }), 'cyan'],
      [cash(after(N)), t('Left after {n} days ({pct}% lost)', { n: N, pct: lostPct }), after(N) > F ? 'gain' : 'loss'],
      contracts !== null ? [t(contracts === 1 ? '{n} contract' : '{n} contracts', { n: contracts }), t('{symbol} per trade', { symbol: c.symbol }), contracts ? '' : 'loss'] : null,
    ]} />
  );
  const extras = ready && (
    <>
      {contracts === 0 && (
        <p className="tool-warning">
          {t('Even 1 {symbol} with a {points}-point stop risks {amount}, more than your {perTrade} per trade. Use a closer stop, trade fewer times a day, or wait until your account can support this contract.',
            { symbol: c.symbol, points: fmt(stopDist, 4), amount: money(riskPer), perTrade: money(perTrade) })}
        </p>
      )}
      <BalanceChart after={after} start={B} floor={F} days={chartDays} goal={N} />
      <button type="button" className="calc-link" aria-expanded={table} onClick={() => setTable(!table)}>{table ? t('Hide the table') : t('Show table')}</button>
      {table && (
        <div className="table-wrap tool-table">
          <table>
            <thead>
              <tr>
                <th scope="col">{t('Losing days in a row')}</th>
                <th scope="col">{t('Balance left')}</th>
                <th scope="col">{t('Account lost')}</th>
              </tr>
            </thead>
            <tbody>
              {milestones.map((k) => {
                const bal = after(k);
                return (
                  <tr key={k} className={k === N ? 'tool-row-best' : ''}>
                    <th scope="row">{k}</th>
                    <td className={bal <= F ? 'loss' : ''}>{money(bal)}</td>
                    <td>{fmt(((B - bal) / B) * 100, 1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="calc-plan-box">
        <span className="calc-section-label">{t('To survive {n} losing days', { n: N })}</span>
        <ul>
          <li>{tr('Fixed limit: at most <0>{amount}</0> a day ({pct}% of your starting balance).', strong, { amount: cash(fixedForGoal), pct: fmt((fixedForGoal / B) * 100, 2) })}</li>
          <li>{F > 0
            ? tr('Shrinking limit: at most <0>{pct}%</0> of your current balance each day.', strong, { pct: fmt(shrinkPctForGoal, 2) })
            : tr('Shrinking limit: at most <0>{pct}%</0> of your current balance each day keeps at least half your account.', strong, { pct: fmt(shrinkPctForGoal, 2) })}</li>
        </ul>
      </div>
    </>
  );
  const save = t('Set your daily max loss once and get warned when you’re close.');

  let answer;
  if (!ready) {
    answer = <Answer tone="wait" text={finding ? t('Enter your balance and how many losing days it should take to see your daily max loss.') : t('Enter your balance and daily max loss to see your plan.')} />;
  } else if (finding) {
    answer = (
      <Answer tone="good" page={page} user={user} save={save}
        title={tr('Lose at most <0>{amount}</0> a day', num0, { amount: cash(fixedForGoal) })}
        text={t('{pct}% of your starting balance a day. {n} losing days in a row {blown}.', { pct: fmt((fixedForGoal / B) * 100, 2), n: N, blown: blownLabel })}>
        {tiles}
        {extras}
      </Answer>
    );
  } else {
    const never = daysToBlow === Infinity;
    answer = (
      <Answer tone={ok ? 'good' : 'bad'} badge={ok ? t('Meets your goal') : t('Short of your goal')} page={page} user={user} save={save}
        title={never ? t('Never fully') : tr(daysToBlow === 1 ? 'Survives <0>{n}</0> losing day' : 'Survives <0>{n}</0> losing days', num0, { n: daysToBlow })}
        text={never ? t('The limit shrinks with your balance, so the account never quite reaches zero.')
          : tr(ok ? 'That beats your goal of <0>{n} days</0> {end}.' : 'That’s short of your goal of <0>{n} days</0> {end}.', strong, { n: N, end: endLabel })}>
        {tiles}
        {extras}
      </Answer>
    );
  }

  const how = (
    <HowItWorks steps={[
      [t('Fixed limit'), valid
        ? t('The same dollars every day: at most {amount} ({pct}% of your starting balance) takes {n} losing days in a row {blown}.', { amount: cash(fixedForGoal), pct: fmt((fixedForGoal / B) * 100, 2), n: N, blown: blownLabel })
        : t('The same dollars every day, based on your starting balance.')],
      [t('Shrinking limit'), valid
        ? (F > 0
          ? t('A % of your current balance, recalculated each day: at most {pct}% a day keeps you above your floor after {n} losing days.', { pct: fmt(shrinkPctForGoal, 2), n: N })
          : t('A % of your current balance, recalculated each day: at most {pct}% a day keeps at least half your account after {n} losing days.', { pct: fmt(shrinkPctForGoal, 2), n: N }))
        : t('A % of your current balance, recalculated each day.')],
      [t('Risk per trade'), ready
        ? t('Your daily max loss ÷ your max trades a day: {daily} ÷ {n} = {per}.', { daily: cash(todayLoss), n: perDay, per: cash(perTrade) })
        : t('Your daily max loss ÷ your max trades a day.')],
    ]} />
  );
  const bar = ready && (
    <PhoneBar tone={finding || ok ? 'good' : 'bad'}
      title={finding ? t('Lose at most {amount} a day', { amount: cash(fixedForGoal) })
        : daysToBlow === Infinity ? t('Never fully') : t(daysToBlow === 1 ? 'Survives {n} losing day' : 'Survives {n} losing days', { n: daysToBlow })}
      sub={`${t('Daily max loss')} ${cash(todayLoss)} · ${t('Risk per trade')} ${cash(perTrade)}`} />
  );
  return <CalcLayout page={page} inputs={inputs} answer={answer} guide={guide} how={how} bar={bar} />;
}

// The article that explains each calculator's topic.
const GUIDES = {
  'position-size': { post: 'position-size-stop-loss-take-profit', lead: 'New to position sizing?', link: 'Read: how to size your trades and place your stop loss and take profit' },
  drawdown: { post: 'trailing-drawdown-explained', lead: 'Not sure how your drawdown trails?', link: 'Read: trailing drawdown explained, with examples' },
  'risk-plan': { post: 'set-your-own-daily-loss-limit', lead: 'New to risk planning?', link: 'Read: how to set your own daily loss limit' },
  consistency: { post: 'consistency-rule-explained', lead: 'New to the consistency rule?', link: 'Read: how the consistency rule works and how to pass it' },
};

const CALCULATORS = { 'position-size': PositionSize, 'risk-plan': RiskPlan, drawdown: Drawdown, consistency: Consistency };

function ToolsNav({ lang, current }) {
  const { t } = useI18n();
  const nav = useRef(null);
  // On phones the tabs scroll sideways: bring the open calculator's tab into view.
  useEffect(() => {
    const active = nav.current?.querySelector('a.active');
    if (active) nav.current.scrollLeft = active.offsetLeft - 16;
  }, [current]);
  return (
    <nav ref={nav} className="tools-nav" aria-label={t('Free calculators')}>
      {TOOLS.map((tool) => (
        <Link key={tool.slug} to={toolPath(lang, tool.slug)} className={tool.slug === current ? 'active' : ''}>{tool[lang].name}</Link>
      ))}
    </nav>
  );
}

function SignupNudge({ user }) {
  const { t } = useI18n();
  return (
    <section className="final-cta tool-cta">
      <h2>{t('Let Market Matrix do this math for you, every day.')}</h2>
      <p>{t('Import your trades and the free journal tracks your daily loss limit, drawdown and consistency rule for you, and shows which setups are worth the risk.')}</p>
      <Link to={user ? '/app' : '/signup'} className="cta-button">{user ? t('Open journal') : t('Start your free journal')} &rarr;</Link>
    </section>
  );
}

export function ToolsIndex({ user, forceLang }) {
  const lang = usePageLanguage(forceLang, '/tools');
  const { t } = useI18n();
  useTitle(`${t('Free trading calculators')} | Market Matrix`);
  return (
    <PublicShell user={user} className="tools-page">
      <div className="page-title">
        <p className="eyebrow">{t('Free tools')}</p>
        <h1>{t('Free trading calculators')}</h1>
        <p className="subtitle">{t('Size your trades and protect your prop firm account. No sign-up needed.')}</p>
      </div>
      <ul className="blog-list tools-list">
        <li>
          <Link to={lang === 'es' ? '/es/report-card' : '/report-card'} className="blog-card tool-card-featured">
            <span className="blog-card-title">{t('Free trading report card')}</span>
            <span className="blog-card-text">{t('Drop in your trade file and get an honest grade on your trading in seconds: what’s working, what’s costing you, and the one thing to fix next.')}</span>
            <span className="blog-card-more">{t('Grade my trades')} &rarr;</span>
          </Link>
        </li>
        {TOOLS.map((tool) => (
          <li key={tool.slug}>
            <Link to={toolPath(lang, tool.slug)} className="blog-card">
              <span className="blog-card-title">{tool[lang].name}</span>
              <span className="blog-card-text">{tool[lang].description}</span>
              <span className="blog-card-more">{t('Open the calculator')} &rarr;</span>
            </Link>
          </li>
        ))}
      </ul>
      <SignupNudge user={user} />
    </PublicShell>
  );
}

// The short line under each calculator's name (the longer description is for search results).
const SUBTITLES = {
  'position-size': 'See how many contracts to trade so a full stop-out costs only what you planned.',
  'risk-plan': 'Set a daily max loss that survives a long losing streak.',
  drawdown: 'Find the most contracts you can trade and still survive a losing streak on your prop account.',
  consistency: 'Check if your best day is too big a share of your profit, and how much more you need to pass.',
};

export function ToolPage({ user, forceLang }) {
  const { slug } = useParams();
  const lang = usePageLanguage(forceLang, `/tools/${slug}`);
  const { t } = useI18n();
  const tool = TOOLS.find((x) => x.slug === slug);
  useTitle(tool ? `${tool[lang].title} | Market Matrix` : 'Market Matrix');
  if (!tool) return <Navigate to={toolPath(lang)} replace />;
  const Calculator = CALCULATORS[slug];
  const guide = GUIDES[slug] && (
    <p className="tool-read-more">
      {t(GUIDES[slug].lead)}{' '}
      <Link to={localPath(lang, `/blog/${GUIDES[slug].post}`)}>{t(GUIDES[slug].link)} &rarr;</Link>
    </p>
  );
  return (
    <PublicShell user={user} className="tools-page tool-page">
      <p className="eyebrow calc-eyebrow"><Link to={toolPath(lang)}>{t('Free tools')}</Link></p>
      <ToolsNav lang={lang} current={slug} />
      <div className="page-title calc-title">
        <h1>{tool[lang].name}</h1>
        <p className="subtitle">{SUBTITLES[slug] ? t(SUBTITLES[slug]) : tool[lang].description}</p>
      </div>
      {/* Typing into the calculator counts as using it (once per visit). */}
      <div onInputCapture={() => trackTool(slug)} onChangeCapture={() => trackTool(slug)} onClickCapture={() => trackTool(slug)}>
        <Calculator user={user} page guide={guide} />
      </div>
      <p className="tool-disclaimer">{t('For education only, not financial advice. Contract values are standard exchange specifications; always check your broker and firm.')}</p>
    </PublicShell>
  );
}
